import SoftooMap from "../Map";
import Map from "ol/Map";
import { Point, LineString, Polygon, MultiPolygon } from "ol/geom";
import Feature from "ol/Feature";
import { Select, Modify } from "ol/interaction";
import { altKeyOnly, singleClick, click } from 'ol/events/condition.js';

import { Coordinate, IMeasureResult, IModifiedFeature } from "../types";
import { Drawer } from "./Drawer";

export class ModifyFeature extends Drawer {
  private $map: SoftooMap;
  private coordinate: Coordinate;
  private readyToStartDraw:boolean;
  private select:Select;
  private modify:Modify;
  
  constructor(options) {
    options = options || {};
    let afterModified;
    if (options.source) {
      delete options.source; // we don't need to keep the drawing
    }
    if (options.afterModified) {
      afterModified = options.afterModified;
      delete options.afterModified;
    }
    let layers, selectStyle, pixelTolerance = 10; //Openlayers default
    if (options.layers) {
      layers = options.layers;
      delete options.layers;
    }
    if (options.selectStyle) {
      selectStyle = options.selectStyle;
      delete options.selectStyle;
    }
    if (options.pixelTolerance) {
      pixelTolerance = options.pixelTolerance;
      delete options.pixelTolerance
    }
    options.type = 'LineString'; // drawing a line to show moving distance
    super(Object.assign({
      maxPoints: 2 // force to stop drawing after adding second point
    }, options));
    
    this.select = new Select({
      layers: layers || null,
      style: selectStyle || null,
      wrapX: false
    });

    this.modify = new Modify({
      features: this.select.getFeatures(),
      pixelTolerance: pixelTolerance,
      style: options.style,
      deleteCondition: (event) => {
        return altKeyOnly(event) && (singleClick(event) || click(event));
      }
    });

    this.startModify_ = (event) => {
      this.startModify(event);
    }
    this.endModify_ = (event) => {
      this.endModify(event);
    }
    if (afterModified) {
      this.afterModified = afterModified;
    }
  }

  private startModify_;
  private endModify_;
  private targets:IModifiedFeature[];
  
  startModify(event) {
    let features = event.features.getArray();
    this.targets = features.map((f) => {
      return {
        original: this.$map.cloneFeature(f),
        modified: f
      }
    });
    this.readyToStartDraw = true;
    this['startDrawing_'](event.mapBrowserEvent); // defined in ol/interaction/Draw.js
  }

  /**
   * We dont support multi* geometry
   * @param target 
   * @param original 
   */
  hasBeenModified(target:Feature,original:Feature) : boolean {
    let modified: boolean = false;
    let tg = target.getGeometry();
    let og = original.getGeometry();
    let tc = tg['getCoordinates']();
    let oc = og['getCoordinates']();
    if (tg instanceof Point) {
      modified = tc[0]!=oc[0] || tc[1]!=oc[1];
    } else if (tg instanceof LineString) {
      modified = (tc.length!=oc.length);
      if (!modified) { // compare each vertex
        for (let i=0; i<tc.length && !modified; i++) {
          modified = (tc[i][0]!=oc[i][0] || tc[i][1]!=oc[i][1]);
        }
      }
    } else if (tg instanceof Polygon) {
      modified = modified = tc.length != oc.length;
      if (!modified) {
        for (let j=0; j<tc.length && !modified; j++) { // loop through N boundaries
          modified = (tc[j].length != oc[j].length);
          if (!modified) {
            for (let i=0; i<tc[j].length && !modified; i++) {
              modified = (tc[j][i][0]!=oc[j][i][0] || tc[j][i][1]!=oc[j][i][1]);
            }
          }
        }
      }
    }
    return modified;
  }

  endModify(event) {
    if (this.targets && this.targets.length>0) {
      try {
        let modifiedFeatures:IModifiedFeature[] = [];
        this.targets.forEach((target,index) => {
          if (this.hasBeenModified(target.modified,target.original)) {
            this.$map.addUndo(target.original, 'Update', index>0);
            target.modified.set('STATE', 'Update');
            modifiedFeatures.push(target);
          }
        });
        if (modifiedFeatures.length>0) {
          this.afterModified(modifiedFeatures);
        }
        /*
        if (modifiedFeatures.length>0) {
          let accept = await this.afterModified(modifiedFeatures);
          if (accept) {
            modifiedFeatures.forEach((target,index) => {
              this.$map.addUndo(target.original, 'Update', index>0);
              target.modified.set('STATE', 'Update');
            });
          } else {
            modifiedFeatures.forEach((target,index) => {
              target.modified.unset('STATE');
              target.modified.setGeometry(target.original.getGeometry());
            });
          }
        }*/
      } catch (error) {
        throw(error);
      }
    }
    this['finishDrawing'](); // defined in ol/interaction/Draw.js
  }

  startDraw(event) {
    if (this.readyToStartDraw!==true) {
      this['abortDrawing_']();
    }
    else {
      if (!this.coordinate) {
        this.coordinate = this['sketchCoords_'][0].map((c) => {
          return c; // IMPORTANT: to clone the coordinate in order to measure distance of move correctly
        });
      }
      super.startDraw(event);
      this.readyToStartDraw = false;
    }
  }
  
  endDraw(event) {
    super.endDraw(event);
  }

  discardDraw() {
    // intentionally blank to avoid undo
  }

  measureDistance(geom:LineString) : IMeasureResult {
    let result = super.measureDistance(geom);
    if (this.targets) {
      this.targets.forEach((target) => {
        let feature = target.modified;
        let geom = feature.getGeometry();
        if (geom instanceof Polygon || geom instanceof MultiPolygon) {
          feature.set('area_sqm', this.calcArea(geom));
          this.calcRaiNganSqwah(feature);
        }
      });
    }
    return result;
  }

  calcRaiNganSqwah(land:Feature) {
    let area:number = land.get('area_sqm');
    if (area || area===0) {
      let rai:number, ngan:number, sqwah:number;
      area = area / 1600.0; //to rai
      rai = Math.trunc(area);
      area = (area - rai) * 4.0; //to ngan
      ngan = Math.trunc(area);
      area = (area - ngan) * 100.0; //to sqwah
      sqwah = Math.round(area);
      if (sqwah>=100) {
        sqwah -= 100;
        ngan += 1;
        if (ngan>=4) {
          ngan -= 1;
          rai += 1;
        }
      }
      land.set('area_rai', rai);
      land.set('area_ngan', ngan);
      land.set('area_sqwah', sqwah);
    }
  }

  afterModified(modified:IModifiedFeature[]) {
    // to be override by application
    return true;
  }
  
  setMap(map:Map) {
    if (map) {
      this.map = map;
      this.$map = map['SOFTOO_MAP'];
      this.modify.on('modifystart', this.startModify_);
      this.modify.on('modifyend', this.endModify_);
      map.addInteraction(this.modify);
      map.addInteraction(this.select);
      super.setMap(map);
    }
  }

  remove() {
    if (this.map) {
      this.modify.un('modifystart', this.startModify_);
      this.modify.un('modifyend', this.endModify_);
      this.map.removeInteraction(this.modify);
      this.map.removeInteraction(this.select);
      super.remove();
    }
  }
}